// feature/draft-persistence.
//
// Defensive form-state persistence. Drop-in replacement for `useState`
// that mirrors every value into localStorage so a crash, reload, network
// drop, accidental tab close, or session expiry doesn't blow the user's
// typed work away.
//
// Triggered by Camilo's stability incident (2026-05-07): browser auto-
// translate caused React DOM crashes on /reparto-insumos, Sprint 41's
// error boundary fired the "Algo salió mal — Recargar" recovery screen,
// he reloaded, and his typed notes/file selection were gone. Sprint 62
// closed the trigger (lang="es" + notranslate); this hook closes the
// underlying class — work no longer vanishes when the page reloads
// for ANY reason.
//
// Shape: `useDraftField(key, initialValue)` → `[value, setValue, clear]`.
//   • On mount, reads from localStorage[key]; falls back to initialValue.
//   • On every setValue, writes to localStorage[key] (best-effort —
//     quota and private-mode errors are swallowed).
//   • clear() removes the key (call after a successful submit).
//   • If `key` is null/undefined the hook degrades to plain useState
//     (no persistence). Useful for callers who derive the key from
//     props that may not be ready on first render.
//
// JSON-serializable values only. Files / Blobs cannot be persisted —
// the caller still has to re-pick the file after a reload, but the
// surrounding text fields (notes, reasons, categorías) survive.

import { useCallback, useEffect, useRef, useState } from "react";

const PREFIX = "eds.draft.";

function read(key) {
  if (!key) return undefined;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw == null) return undefined;
    return JSON.parse(raw);
  } catch (_) {
    return undefined;
  }
}

function write(key, value) {
  if (!key) return;
  try { localStorage.setItem(PREFIX + key, JSON.stringify(value)); }
  catch (_) { /* quota / private mode — ignore */ }
}

function remove(key) {
  if (!key) return;
  try { localStorage.removeItem(PREFIX + key); }
  catch (_) { /* ignore */ }
}

export default function useDraftField(key, initialValue) {
  // First render: try to hydrate from localStorage. If the key is null
  // (e.g. props not ready yet), fall back to initialValue and rehydrate
  // when the key becomes available.
  const [value, setValue] = useState(() => {
    const persisted = read(key);
    return persisted !== undefined ? persisted : initialValue;
  });

  // Track the last key so we can rehydrate when it changes (e.g. the
  // form remounts under a different case_id).
  const lastKeyRef = useRef(key);
  useEffect(() => {
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      const persisted = read(key);
      setValue(persisted !== undefined ? persisted : initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setAndPersist = useCallback((next) => {
    setValue((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      write(key, resolved);
      return resolved;
    });
  }, [key]);

  const clear = useCallback(() => {
    remove(key);
    setValue(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return [value, setAndPersist, clear];
}

// Bulk-clear helper for forms with multiple draft fields. Call on
// successful submit so the next time the user opens the form they
// see a fresh slate, not the stale draft from the previous case.
export function clearDraftKeys(keys) {
  for (const k of keys) remove(k);
}
